import { ChangeEvent, FormEvent, useState } from "react";
import { Box, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material";
import { BackButton, NextButton } from "src/components/application/styled-components/Buttons";
import {
  StyledErrorField,
  StyledTextField,
} from "src/components/application/styled-components/AddressFields";
import {
  AdhocKycAnswer,
  AdhocKycAuth,
  AdhocKycContextResponse,
  AdhocKycQuestion,
  AdhocKycQuestionSchema,
  adhocKycErrorMessage,
  useAdhocKycQuestionsMutation,
} from "src/store/features/api/adhocKyc";
import { AdhocKycBody, AdhocKycError, AdhocKycHeading, ADHOC_KYC_NAVY } from "./AdhocKycShell";

export interface QuestionsStepProps {
  auth: AdhocKycAuth;
  context: AdhocKycContextResponse;
  /** Re-pulls the context when the schema hasn't arrived yet. */
  onRefresh: () => void;
}

export type AnswerValue = string | string[] | boolean;
export type Answers = Record<string, AnswerValue>;

/** Whether a question has a usable answer, by type. */
export const isAnswered = (
  question: AdhocKycQuestion,
  answer: AnswerValue | undefined,
): boolean => {
  switch (question.type) {
    case "multi":
      return Array.isArray(answer) && answer.length > 0;
    case "confirm":
      return typeof answer === "boolean";
    default:
      return typeof answer === "string" && answer.trim().length > 0;
  }
};

/**
 * Whether the schema skips a question for the current answers: its `skipWhen`
 * rule names a single-choice question whose answer is one of the rule's
 * values (e.g. industry when the employment status is retired). A skipped
 * question is not rendered, not required and not sent.
 */
export const isSkipped = (question: AdhocKycQuestion, answers: Answers): boolean => {
  const rule = question.skipWhen;
  if (!rule) return false;
  const trigger = answers[rule.questionId];
  return typeof trigger === "string" && rule.values.includes(trigger);
};

export const validateAnswers = (
  schema: AdhocKycQuestionSchema,
  answers: Answers,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  schema.questions.forEach((question) => {
    if (isSkipped(question, answers)) return;
    if (question.required && !isAnswered(question, answers[question.id])) {
      errors[question.id] = "This question is required";
    }
  });
  return errors;
};

/**
 * Only answered, applicable questions are sent — optional blanks are omitted,
 * not sent as "", and a skipped question's answer (e.g. the industry
 * pre-filled from file) stays behind.
 */
export const toAnswerList = (schema: AdhocKycQuestionSchema, answers: Answers): AdhocKycAnswer[] =>
  schema.questions
    .filter(
      (question) => !isSkipped(question, answers) && isAnswered(question, answers[question.id]),
    )
    .map((question) => ({ questionId: question.id, answer: answers[question.id] }));

/**
 * Answers the server pre-fills: a single-choice question whose `prefill` (the
 * held value, e.g. the industry on file) matches one of its options by value
 * or label, case-insensitively. Anything the customer picks overrides it.
 */
export const prefilledAnswers = (schema: AdhocKycQuestionSchema | undefined): Answers => {
  const out: Answers = {};
  (schema?.questions || []).forEach((question) => {
    if (question.type !== "single" || !question.prefill) return;
    const wanted = question.prefill.trim().toLowerCase();
    const match = (question.options || []).find(
      (option) =>
        option.value.toLowerCase() === wanted || option.label.trim().toLowerCase() === wanted,
    );
    if (match) out[question.id] = match.value;
  });
  return out;
};

/**
 * The label the customer sees: optional questions are marked "(optional)"
 * once, even when the seeded label already ends that way (it did on v2 and
 * testers saw "(optional) (optional)").
 */
export const questionLabel = (question: AdhocKycQuestion): string => {
  const label = question.label.trim();
  if (question.required || /\(optional\)$/i.test(label)) return label;
  return `${label} (optional)`;
};

const toggleInList = (list: AnswerValue | undefined, value: string): string[] => {
  const current = Array.isArray(list) ? list : [];
  return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
};

/**
 * Step 4. Renders entirely from `context.questionSchema` (option lists ship
 * inside it — the industries/occupation endpoints need a session). Questions
 * the schema skips for the current answers disappear from the form. Answers
 * go back with the schema version so the backend validates against the same
 * question set the customer saw.
 */
export default function QuestionsStep({
  auth,
  context,
  onRefresh,
}: QuestionsStepProps): JSX.Element {
  const [picked, setAnswers] = useState<Answers>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submit, { isLoading, error }] = useAdhocKycQuestionsMutation();
  const schema = context.questionSchema;
  // Pre-filled answers sit under whatever the customer has picked, so a schema that
  // arrives after a refresh still pre-selects without a remount.
  const answers: Answers = { ...prefilledAnswers(schema), ...picked };

  if (!schema) {
    return (
      <Box data-testid="adhoc-kyc-questions-missing">
        <AdhocKycHeading>A few quick questions</AdhocKycHeading>
        <AdhocKycError message="The questions aren't available yet. Please try again in a moment." />
        <BackButton fullWidth onClick={onRefresh} data-testid="adhoc-kyc-questions-refresh">
          Try again
        </BackButton>
      </Box>
    );
  }

  const setAnswer = (id: string, value: AnswerValue): void =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  const handleText = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
    setAnswer(event.target.name, event.target.value);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextErrors = validateAnswers(schema, answers);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    submit({
      auth,
      body: { schemaVersion: schema.version, answers: toAnswerList(schema, answers) },
    });
  };

  const renderQuestion = (question: AdhocKycQuestion): JSX.Element => {
    const answer = answers[question.id];
    const label = questionLabel(question);
    let control: JSX.Element;
    switch (question.type) {
      case "single":
        control = (
          <StyledTextField
            select
            name={question.id}
            label={label}
            value={typeof answer === "string" ? answer : ""}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": `adhoc-kyc-question-${question.id}` }}
            onChange={handleText}
            error={Boolean(errors[question.id])}
            selected={Boolean(answer)}
          >
            <option value="" />
            {(question.options || []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </StyledTextField>
        );
        break;
      case "multi":
        control = (
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: ADHOC_KYC_NAVY, mb: 0.5 }}>
              {label}
            </Typography>
            <FormGroup data-testid={`adhoc-kyc-question-${question.id}`}>
              {(question.options || []).map((option) => (
                <FormControlLabel
                  key={option.value}
                  label={option.label}
                  control={
                    <Checkbox
                      checked={Array.isArray(answer) && answer.includes(option.value)}
                      onChange={() => setAnswer(question.id, toggleInList(answer, option.value))}
                      inputProps={{ "aria-label": option.label }}
                    />
                  }
                />
              ))}
            </FormGroup>
          </Box>
        );
        break;
      case "confirm":
        control = (
          <Box data-testid={`adhoc-kyc-question-${question.id}`}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: ADHOC_KYC_NAVY, mb: 0.5 }}>
              {label}
            </Typography>
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "16px",
                p: 2,
                mb: 1,
                bgcolor: "#FFFFFF",
                fontSize: "14px",
              }}
            >
              {question.prefill}
            </Box>
            <Stack direction="row" spacing={1}>
              <BackButton
                onClick={() => setAnswer(question.id, true)}
                sx={answer === true ? { borderColor: "#0BEC9F", background: "#E6FFF6" } : undefined}
                data-testid={`adhoc-kyc-confirm-yes-${question.id}`}
              >
                {answer === true ? "Confirmed" : "Confirm"}
              </BackButton>
              <BackButton
                onClick={() => setAnswer(question.id, false)}
                sx={
                  answer === false ? { borderColor: "#FC4C4C", background: "#FFF1F1" } : undefined
                }
                data-testid={`adhoc-kyc-confirm-no-${question.id}`}
              >
                This has changed
              </BackButton>
            </Stack>
          </Box>
        );
        break;
      default:
        control = (
          <StyledTextField
            name={question.id}
            label={label}
            value={typeof answer === "string" ? answer : ""}
            multiline
            minRows={2}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": `adhoc-kyc-question-${question.id}` }}
            onChange={handleText}
            error={Boolean(errors[question.id])}
            selected={Boolean(answer)}
          />
        );
    }
    return (
      <Box key={question.id} sx={{ mb: 2.5 }}>
        {control}
        {errors[question.id] ? (
          <StyledErrorField role="alert">{errors[question.id]}</StyledErrorField>
        ) : null}
      </Box>
    );
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} data-testid="adhoc-kyc-questions-step">
      <AdhocKycHeading>A few quick questions</AdhocKycHeading>
      <AdhocKycBody>
        These help us keep your account profile accurate. Your identity check runs in the background
        — you don&rsquo;t need to wait for it.
      </AdhocKycBody>
      {schema.questions.filter((question) => !isSkipped(question, answers)).map(renderQuestion)}
      {error ? <AdhocKycError message={adhocKycErrorMessage(error)} /> : null}
      <NextButton
        fullWidth
        type="submit"
        disabled={isLoading}
        data-testid="adhoc-kyc-questions-submit"
      >
        Submit
      </NextButton>
    </Box>
  );
}
